import { Download } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useCallback } from 'react'

import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'

import { isPWA, isIos } from '@/lib/utils'
import { usePwaStore } from '@/stores/pwa-store'

const PwaInstallationTrigger = () => {
  const tComponent = useTranslations('Pwa.components.pwa_installation_trigger')
  const {
    promptEvent,
    isInstallableByPrompt,
    isWaitingForBeforeInstall,
    isInstalling,
    clearPromptEvent,
    setIsInstallableByPrompt,
    setIsInstalling,
  } = usePwaStore()

  const handleInstallAppClick = useCallback(async () => {
    if (isInstallableByPrompt && promptEvent) {
      // show the browser install prompt
      promptEvent.prompt()

      // wait for the user's choice
      const { outcome } = await promptEvent.userChoice

      // the prompt can only be used once
      clearPromptEvent()

      // hide the install button after the prompt has been shown
      setIsInstallableByPrompt(false)

      if (outcome === 'accepted') {
        setIsInstalling(true)
      }
    }
  }, [isInstallableByPrompt, promptEvent, clearPromptEvent, setIsInstallableByPrompt, setIsInstalling])

  if (isWaitingForBeforeInstall || isInstalling) {
    return <Skeleton className="h-12" />
  }

  if (isPWA()) {
    return (
      <div className="rounded-lg bg-base-accent p-4">
        <h4 className="mb-1 text-base font-medium text-foreground">{tComponent('messages.already_installed.title')}</h4>
        <p className="text-sm font-normal text-muted-foreground">
          {tComponent('messages.already_installed.description')}
        </p>
      </div>
    )
  }

  if (isInstallableByPrompt) {
    return (
      <Button onClick={handleInstallAppClick}>
        <Download className="me-2 size-4" />
        {tComponent('actions.install')}
      </Button>
    )
  }

  if (isIos()) {
    return (
      <div className="rounded-lg bg-base-accent p-4">
        <h4 className="mb-1 text-base font-medium text-foreground">
          {tComponent('messages.ios_installation_instruction.title')}
        </h4>

        <p className="mb-4 text-sm font-normal text-muted-foreground">
          {tComponent('messages.ios_installation_instruction.description')}
        </p>
        <ol className="list-decimal space-y-2 pl-6 text-sm font-normal text-muted-foreground">
          <li>{tComponent('messages.ios_installation_instruction.steps.step_1')}</li>
          <li>{tComponent('messages.ios_installation_instruction.steps.step_2')}</li>
          <li>{tComponent('messages.ios_installation_instruction.steps.step_3')}</li>
        </ol>
      </div>
    )
  }

  return (
    <div className="rounded-lg bg-base-accent p-4">
      <h4 className="mb-1 text-base font-medium text-foreground">{tComponent('messages.not_supported.title')}</h4>
      <p className="text-sm font-normal text-muted-foreground">{tComponent('messages.not_supported.description')}</p>
    </div>
  )
}
PwaInstallationTrigger.displayName = 'PwaInstallationTrigger'

export { PwaInstallationTrigger }
